import { motion } from "framer-motion";
import { FileText, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserPrompts } from "@/hooks/prompts/use-user-prompts";
import { ProfilePromptCard } from "./ProfilePromptCard";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useCallback } from "react";

interface ProfilePromptsGridProps {
  userId: string;
  isOwnProfile?: boolean;
}

export function ProfilePromptsGrid({ userId, isOwnProfile = false }: ProfilePromptsGridProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  
  const { 
    prompts, 
    loading, 
    loadingMore,
    hasMore,
    loadMore,
    updatePrompt, 
    deletePrompt,
    totalCount
  } = useUserPrompts(userId, user?.id);

  // Infinite scroll observer
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const target = entries[0];
    if (target.isIntersecting && hasMore && !loadingMore) {
      loadMore();
    }
  }, [hasMore, loadingMore, loadMore]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    });

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-24" />
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (prompts.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center py-16 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
          <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {isOwnProfile ? "No prompts yet" : "No prompts shared"}
        </h3>
        <p className="text-muted-foreground text-sm max-w-sm mb-6">
          {isOwnProfile 
            ? "Start sharing your AI prompts with the community. Create your first prompt to get started!"
            : "This user hasn't shared any prompts yet. Check back later!"
          }
        </p>
        {isOwnProfile && (
          <Button onClick={() => navigate("/create-prompt")} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Your First Prompt
          </Button>
        )}
      </motion.div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Prompt Count Header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-between"
      >
        <span className="text-sm text-muted-foreground font-medium">
          {totalCount} {totalCount === 1 ? 'prompt' : 'prompts'}
        </span>
      </motion.div>

      {/* Prompts Grid */}
      <div className="space-y-4">
        {prompts.map((prompt, index) => (
          <motion.div
            key={prompt.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <ProfilePromptCard
              prompt={prompt}
              onUpdate={updatePrompt}
              onDelete={deletePrompt}
            />
          </motion.div>
        ))}
      </div>

      {/* Load More Trigger */}
      <div ref={loadMoreRef} className="py-4 flex justify-center">
        {loadingMore && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading more...</span>
          </div>
        )}
      </div>
    </div>
  );
}
